import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { assistantService, sessionService, integrationService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { WhatsAppSession } from '../types/api';
import { useSession } from '../components/whatsapp/hooks/useSession';
import { getQrCodeUrl } from '../components/whatsapp/constants';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import SessionList from '../components/whatsapp/SessionList';
import SessionForm from '../components/whatsapp/SessionForm';
import Modal from '../components/ui/Modal';
import QRCodeModal from '../components/ui/QRCodeModal';

type ModalType = '' | 'create' | 'edit' | 'qr';

interface ModalState {
  type: ModalType;
  title?: string;
}

function Sessions() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingSession, setEditingSession] = useState<WhatsAppSession | null>(null);
  const user = useAuthStore(state => state.user);

  const { data: assistants } = useQuery({
    queryKey: ['assistants'],
    queryFn: () => assistantService.getAssistants(),
    staleTime: 60000, // 1 minute
    retry: 3,
  });

  const { data: integrationsResponse } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationService.getIntegrations(),
    staleTime: 60000, // 1 minute
    retry: 3,
  });

  const integrations = Array.isArray(integrationsResponse) ? integrationsResponse : integrationsResponse?.data || [];

  const { sessions, timeConditions, currentQrSession, qrCodeData, isLoadingQr, qrError, mutations, qrCode } =
    useSession(user?.id || 0);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const assistantId = formData.get('assistant_id');
    const integrationId = formData.get('integration_id');
    const messagesPerMinute = formData.get('messages_per_minute');
    const messagesPerHour = formData.get('messages_per_hour');
    const messagesTimeConditionId = formData.get('messages_time_condition_id');
    const smtpPort = formData.get('smtp_port');
    const sendAlertAt = formData.get('send_alert_at') as string;

    const baseData = {
      assistant_id: assistantId ? Number(assistantId) : null,
      integration_id: integrationId ? Number(integrationId) : null,
      isDefault: formData.get('isDefault') === 'true',
      token: formData.get('token') as string,
      engine: formData.get('engine') as 'GMAIL' | 'TICKETZ' | 'SMTP' | 'DIGISAC' | 'GOWS' | 'OPA' | 'INTERNAL' | 'TWILLIO',
      channel: formData.get('channel') as string || 'all',
      messages_per_minute: messagesPerMinute ? Number(messagesPerMinute) : 2,
      messages_per_hour: messagesPerHour ? Number(messagesPerHour) : 20,
      messages_time_condition_id: messagesTimeConditionId ? Number(messagesTimeConditionId) : null,
      send_alert_at: sendAlertAt || undefined,
      smtp_host: formData.get('smtp_host') as string || undefined,
      smtp_port: smtpPort ? Number(smtpPort) : undefined,
      smtp_username: formData.get('smtp_username') as string || undefined,
      smtp_password: formData.get('smtp_password') as string || undefined,
      smtp_secure: formData.get('smtp_secure') === 'true',
      voice_number: formData.get('voice_number') as string || undefined,
    };

    if (editingSession) {
      const data = {
        ...editingSession,
        ...baseData,
      };
      mutations.update.mutate(data, {
        onSuccess: () => {
          messageHandler.success('Sessão atualizada com sucesso!');
          closeModal();
        },
        onError: error => {
          messageHandler.error(error);
        },
      });
    } else {
      const name = formData.get('session_name') as string;
      const formattedName = `${user?.id}_${name.replace(/\s+/g, '_')}`;

      const data = {
        session_name: formattedName,
        ...baseData,
      };
      mutations.create.mutate(data, {
        onSuccess: () => {
          messageHandler.success('Sessão criada com sucesso!');
          closeModal();
        },
        onError: error => {
          messageHandler.error(error);
        },
      });
    }
  };

  const handleDelete = (session: WhatsAppSession) => {
    messageHandler.confirm('Excluir Sessão', 'Tem certeza que deseja excluir esta sessão?', () =>
      mutations.delete.mutate(session.session_name, {
        onSuccess: () => {
          messageHandler.success('Sessão excluída com sucesso!');
        },
        onError: error => {
          messageHandler.error(error);
        },
      })
    );
  };

  const handleStart = (session: WhatsAppSession) => {
    // Only allow start for GOWS engine
    if (!session.engine || session.engine === 'GOWS') {
      messageHandler.confirm('Iniciar Sessão', 'Deseja iniciar esta sessão?', () =>
        mutations.start.mutate(session.session_name, {
          onSuccess: () => {
            messageHandler.success('Sessão iniciada com sucesso!');
          },
          onError: error => {
            messageHandler.error(error);
          },
        })
      );
    }
  };

  const handleStop = (session: WhatsAppSession) => {
    // Only allow stop for GOWS engine
    if (!session.engine || session.engine === 'GOWS') {
      messageHandler.confirm('Parar Sessão', 'Deseja parar esta sessão?', () =>
        mutations.stop.mutate(session.session_name, {
          onSuccess: () => {
            messageHandler.success('Sessão parada com sucesso!');
          },
          onError: error => {
            messageHandler.error(error);
          },
        })
      );
    }
  };

  const handleRestart = (session: WhatsAppSession) => {
    // Only allow restart for GOWS engine
    if (!session.engine || session.engine === 'GOWS') {
      messageHandler.confirm('Reiniciar Sessão', 'Deseja reiniciar esta sessão?', () =>
        mutations.restart.mutate(session.session_name, {
          onSuccess: () => {
            messageHandler.success('Sessão reiniciada com sucesso!');
          },
          onError: error => {
            messageHandler.error(error);
          },
        })
      );
    }
  };

  const handleShowQrCode = (session: WhatsAppSession) => {
    // Only allow QR code for GOWS engine
    if (!session.engine || session.engine === 'GOWS') {
      qrCode.startPolling(session.session_name);
      setModal({ type: 'qr' });
    }
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingSession(null);
    qrCode.stopPolling();
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Conexões"
          description="Lista de todas as conexões e seus status."
          actions={
            <Button
              onClick={() => {
                setEditingSession(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Sessão
            </Button>
          }
        />

        <SessionList
          sessions={sessions || []}
          onEdit={session => {
            setEditingSession(session);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
          onStart={handleStart}
          onStop={handleStop}
          onRestart={handleRestart}
          onShowQrCode={handleShowQrCode}
          onCreateNew={() => {
            setEditingSession(null);
            setModal({ type: 'create' });
          }}
        />

        {/* Create/Edit Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingSession ? 'Editar Sessão' : 'Nova Sessão'}
        >
          <SessionForm
            key={editingSession?.id || 'new'}
            session={editingSession}
            assistants={assistants || []}
            integrations={integrations || []}
            timeConditions={timeConditions || []}
            userId={user?.id || 0}
            onSubmit={handleSubmit}
            isLoading={mutations.create.isPending || mutations.update.isPending}
            onClose={closeModal}
          />
        </Modal>

        {/* QR Code Modal */}
        <QRCodeModal
          isOpen={modal.type === 'qr'}
          onClose={closeModal}
          sessionName={currentQrSession || ''}
          qrCodeUrl={qrCodeData?.url ? getQrCodeUrl(qrCodeData.url) : undefined}
          isLoading={isLoadingQr}
          error={qrError}
        />
      </div>
    </div>
  );
}

export default Sessions;
